/**
 * components/auth/UserManager.tsx
 * Admin-only users manager: add user, change password, change role, remove.
 * Now includes per-store access control (storeIds) for entry/viewer users.
 */

import React, { useMemo, useState } from 'react'
import { UserRole } from '../../store/auth'
import { useUserStore } from '@/store/user'

/** Role label helper */
function roleLabel(role: UserRole) {
  if (role === 'admin') return 'مسؤول كامل'
  if (role === 'viewer') return 'مشاهدة فقط'
  return 'مستخدم إدخال'
}

/**
 * UserManager
 * Provides basic CRUD for users. Only render for admins.
 * Adds per-store assignment for entry/viewer users.
 */
export  function UserManager() {

  // const currentUser = useUserStore((s) => ({username: s.username, role: s.role, id: s.userId}))
  const myrole = useUserStore((s) => s.role)
  const myUsername = useUserStore((s) => s.username)
  const userId = useUserStore((s) => s.userId)
  const currentUser = { username: myUsername, role: myrole, id: userId }

  if (!currentUser || currentUser.role !== 'admin') return null
  
  const users =  useUserStore((s) => s.users)
  
  const addUser = useUserStore((s) => s.addUser)
  const updateUser = useUserStore((s) => s.updateUser)
  const changePassword = useUserStore((s) => s.changePassword)
  const removeUser = useUserStore((s) => s.removeUser)


  const stores = useUserStore((s) => s.stores)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState<UserRole>('entry')
  const [message, setMessage] = useState<string | null>(null)
  const [selectedStoreIds, setSelectedStoreIds] = useState<string[]>([])

   const sorted = useMemo(() => [...users].sort((a, b) => (a.created_at > b.created_at ? 1 : -1)), [users])

  const storesList = useMemo(
    () =>
      Object.values(stores)
        .sort((a, b) => (a.created_at > b.created_at ? 1 : -1))
        .map((m) => ({ id: m.id, name: m.name })),
    [stores]
  )

 

  const onAdd = async () => {
    const res = await addUser(
      username,
      password,
      role,
      selectedStoreIds
    )
    if (!res.ok) {
      setMessage(res.reason || 'فشل الإضافة')
    } else {
      setMessage('تمت الإضافة')
      setUsername('')
      setPassword('')
      setRole('entry')
      setSelectedStoreIds([])
    }
  }

  const onChangePassword = async (id: string) => {
    const newPass = prompt('أدخل كلمة المرور الجديدة')
    if (!newPass) return
    await changePassword(id, newPass)
    setMessage('تم تغيير كلمة المرور')
  }

  const onChangeRole = async (id: string, newRole: UserRole) => {
    await updateUser(id, { role: newRole })
    setMessage('تم تحديث الصلاحية')
  }

  const onRename = async (id: string) => {
    const newName = prompt('أدخل اسم المستخدم الجديد')
    if (!newName) return
    await updateUser(id, { username: newName })
    setMessage('تم تغيير الاسم')
  }

  const onRemove = (id: string) => {
    const ok = confirm('حذف المستخدم؟')
    if (!ok) return
    removeUser(id)
    setMessage('تم الحذف')
  }

  const toggleAssign = async (uid: string, storeId: string, checked: boolean) => {
    const user = users.find((u) => u.id === uid)
    if (!user) return
    const current = new Set(user.storeIds || [])
    if (checked) current.add(storeId)
    else current.delete(storeId)
    await updateUser(uid, { storeIds: Array.from(current) })
    setMessage('تم تحديث صلاحيات المتاجر')
  }

  const onAddToggleStore = (storeId: string, checked: boolean) => {
    const set = new Set(selectedStoreIds)
    if (checked) set.add(storeId)
    else set.delete(storeId)
    setSelectedStoreIds(Array.from(set))
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-lg font-semibold">إدارة المستخدمين</h3>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-5">
        <input
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="اسم المستخدم"
          className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
        />
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="كلمة المرور"
          className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
        />
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as UserRole)}
          className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
        >
          <option value="entry">مستخدم إدخال</option>
          <option value="viewer">مشاهدة فقط</option>
          <option value="admin">مسؤول كامل</option>
        </select>
        <button onClick={onAdd} className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800">
          إضافة مستخدم
        </button>
        <div className="flex items-center text-xs text-slate-500">
          الدور يحدد الصلاحيات. غير المدير يجب تحديد المتاجر المسموح بها.
        </div>
      </div>

      {/* Store assignment for new entry/viewer user */}
      {role !== 'admin' && (
        <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
          <div className="mb-2 text-sm text-slate-700">اختر المتاجر المسموح بها لهذا المستخدم:</div>
          <div className="flex flex-wrap gap-3">
            {storesList.length === 0 ? (
              <span className="text-sm text-slate-500">لا توجد متاجر بعد.</span>
            ) : (
              storesList.map((s) => (
                <label key={s.id} className="inline-flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={selectedStoreIds.includes(s.id)}
                    onChange={(e) => onAddToggleStore(s.id, e.target.checked)}
                  />
                  {s.name}
                </label>
              ))
            )}
          </div>
        </div>
      )}

      {message && <div className="mb-3 text-sm text-slate-600">{message}</div>}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-3 py-2 text-right font-medium text-gray-700">الاسم</th>
              <th className="px-3 py-2 text-right font-medium text-gray-700">الصلاحية</th>
              <th className="px-3 py-2 text-right font-medium text-gray-700">المتاجر</th>
              <th className="px-3 py-2 text-right font-medium text-gray-700">إجراءات</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {sorted.map((u) => (
              <tr key={u.id}>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900">{u.username}</span>
                    {u.id === currentUser.id && (
                      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700">أنت</span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <select
                    value={u.role}
                    onChange={(e) => onChangeRole(u.id, e.target.value as UserRole)}
                    className="rounded border border-slate-300 bg-white px-2 py-1 text-sm"
                  >
                    <option value="entry">مستخدم إدخال</option>
                    <option value="viewer">مشاهدة فقط</option>
                    <option value="admin">مسؤول كامل</option>
                  </select>
                </td>
                <td className="px-3 py-2">
                  {u.role === 'admin' ? (
                    <span className="text-slate-700">جميع المتاجر</span>
                  ) : (
                    <div className="flex flex-wrap gap-3">
                      {storesList.length === 0 ? (
                        <span className="text-slate-500">لا توجد متاجر.</span>
                      ) : (
                        storesList.map((s) => {
                          const checked = Boolean(u.storeIds?.includes(s.id))
                          return (
                            <label key={s.id} className="inline-flex items-center gap-2 text-slate-700">
                              <input
                                type="checkbox"
                                className="h-4 w-4"
                                checked={checked}
                                onChange={(e) => toggleAssign(u.id, s.id, e.target.checked)}
                              />
                              <span className="text-sm">{s.name}</span>
                            </label>
                          )
                        })
                      )}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onRename(u.id)}
                      className="rounded bg-indigo-600 px-3 py-1 text-white hover:bg-indigo-700"
                    >
                      إعادة تسمية
                    </button>
                    <button
                      onClick={() => onChangePassword(u.id)}
                      className="rounded bg-amber-600 px-3 py-1 text-white hover:bg-amber-700"
                    >
                      تغيير كلمة المرور
                    </button>
                    <button
                      onClick={() => onRemove(u.id)}
                      className="rounded bg-rose-600 px-3 py-1 text-white hover:bg-rose-700"
                    >
                      حذف
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-6 text-center text-gray-500">
                  لا يوجد مستخدمون.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
